import { Link } from 'react-router-dom';
import { CREDIT_LOW_MINUTES } from '@klaro7/shared-types';

/**
 * Countdown del credito/tempo residuo mostrato al CLIENTE (chi paga) durante
 * una sessione a pagamento — sempre in vista sia in chiamata che in chat.
 * Componente unico riusabile con 3 stati:
 *   - 'normal'    → > CREDIT_LOW_MINUTES minuti: chip neutro coi minuti residui
 *   - 'warning'   → ]0, CREDIT_LOW_MINUTES]: lampeggia (ambra) + CTA "Ricarica"
 *   - 'exhausted' → <= 0: rosso "Credito esaurito" + CTA "Ricarica"
 *
 * NB: è la superficie del CLIENTE (mostra i propri minuti e può ricaricare). Il
 * professionista usa ClientRunway (minuti soltanto, mai €).
 */

export type CreditCountdownState = 'normal' | 'warning' | 'exhausted';

/** Stato del countdown a partire dai minuti residui (soglia CREDIT_LOW_MINUTES). */
export function creditCountdownState(remainingMinutes: number): CreditCountdownState {
  if (remainingMinutes <= 0) return 'exhausted';
  if (remainingMinutes <= CREDIT_LOW_MINUTES) return 'warning';
  return 'normal';
}

export function CreditCountdown({
  remainingMinutes,
  compact = false,
}: {
  remainingMinutes: number;
  /** Layout compatto (header chat): CTA inline più piccola. */
  compact?: boolean;
}) {
  const state = creditCountdownState(remainingMinutes);
  const minutes = Math.max(0, remainingMinutes);

  if (state === 'normal') {
    return (
      <span className={`text-white/70 ${compact ? 'text-[11px]' : 'text-sm'}`}>
        {minutes} min di credito
      </span>
    );
  }

  const isExhausted = state === 'exhausted';
  const label = isExhausted
    ? 'Credito esaurito'
    : `${minutes} min di credito rimasti`;

  return (
    <span className={`inline-flex items-center gap-2 ${compact ? 'text-[11px]' : 'text-sm'}`}>
      <span
        className={`animate-pulse font-semibold ${isExhausted ? 'text-red-400' : 'text-amber-400'}`}
      >
        {isExhausted ? '⚠️ ' : '⏳ '}
        {label}
      </span>
      {/* target="_blank": CallScreen è un overlay globale fixed z-[90] sempre
          in vista mentre la chiamata è attiva, indipendente dalla route —
          una navigazione in-tab a /my/wallet cambia l'URL ma resta coperta
          e intoccabile dallo stesso overlay (caso reale 26/08, Laura non
          riusciva a toccare i tasti). Nuova scheda = il layer non c'entra. */}
      <Link
        to="/my/wallet"
        target="_blank"
        rel="noopener noreferrer"
        className={`animate-pulse rounded-full bg-brand-orange font-semibold text-white hover:bg-brand-orange/90 ${
          compact ? 'px-2 py-0.5 text-[10px]' : 'px-2.5 py-1 text-xs'
        }`}
      >
        Ricarica
      </Link>
    </span>
  );
}
